import { useGetIdentity, useGetOne, useNotify, useTranslate } from "ra-core";
import { useLocation, useNavigate } from "react-router";

import { Create } from "@/components/admin/create";
import { SimpleForm } from "@/components/admin/simple-form";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import type { Sale } from "@/components/atomic-crm/types";

import { fueReclamada } from "../datos/dataProvider";
import { ETAPAS_ALTA } from "../dominio/etapas";
import type { Etapa, Negocio } from "../dominio/tipos";
import { NegocioInputs } from "./NegocioInputs";
import { comoValidador, validarAlta } from "./validacion";

/** Etapa inicial pedida por la URL (`/negocios/create?etapa=en_conversacion`, desde el pipeline). */
const etapaDeUrl = (search: string): Etapa => {
  const etapa = new URLSearchParams(search).get("etapa");
  return etapa && (ETAPAS_ALTA as string[]).includes(etapa)
    ? (etapa as Etapa)
    : "nuevo";
};

/**
 * Alta de una ficha de negocio. En móvil, formulario compacto (libreta de campo). Desde
 * el pipeline se llega con `?etapa=…` para crear la ficha directamente en esa columna.
 */
export const NegocioCreate = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const notify = useNotify();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { identity, isPending: cargandoIdentidad } = useGetIdentity();
  // Si es administrador (para el origen por defecto), antes de montar el formulario.
  const { data: yo, isPending: cargandoYo } = useGetOne<Sale>(
    "sales",
    { id: identity?.id as number },
    { enabled: !!identity?.id, staleTime: 5 * 60 * 1000 },
  );
  const esAdmin = yo?.administrator === true;

  // El formulario no se monta hasta conocer la identidad: si los valores por defecto
  // cambian después (asignado_a), react-hook-form reinicia el formulario y borra lo que
  // el usuario haya tecleado en los primeros instantes.
  if (cargandoIdentidad || (!!identity?.id && cargandoYo)) return null;

  const valoresPorDefecto: Partial<Negocio> = {
    // Auditoría 30/10/2026: lo que da de alta un setter casi siempre es suyo.
    origen: esAdmin ? "puerta_fria" : "propio_comercial",
    etapa: etapaDeUrl(search),
    es_decisor: false,
    no_llamar: false,
    intentos: 0,
    asignado_a: identity?.id ?? null,
  };

  const formulario = (
    <Create
      resource="negocios"
      disableBreadcrumb
      title={false}
      redirect={false}
      mutationOptions={{
        onSuccess: (data: Pick<Negocio, "id">) => {
          // S10b: ya estaba en el CRM como descubierto de Prospect y sin dueño: ahora es suyo.
          notify(
            fueReclamada(data.id)
              ? "nevora.negocios.reclamada"
              : "nevora.negocios.creada",
            { type: "success" },
          );
          // Vuelve a donde se creó (Pipeline si nace fuera de Leads) con la ficha abierta.
          const destino =
            etapaDeUrl(search) === "nuevo" ? "/negocios" : "/pipeline";
          navigate(`${destino}?ficha=${data.id}`, { replace: true });
        },
      }}
    >
      <SimpleForm
        defaultValues={valoresPorDefecto}
        validate={comoValidador(validarAlta)}
      >
        <NegocioInputs compacto={isMobile} />
      </SimpleForm>
    </Create>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.negocios.nueva_ficha")}
          </h1>
        </MobileHeader>
        <MobileContent>
          <p className="text-sm text-muted-foreground mb-4">
            {translate("nevora.negocios.alta_rapida_ayuda")}
          </p>
          {formulario}
        </MobileContent>
      </>
    );
  }

  return (
    <div className="max-w-4xl w-full mx-auto mt-4">
      <h1 className="titulo-pagina mb-4">
        {translate("nevora.negocios.nueva_ficha")}
      </h1>
      <Card>
        <CardContent className="pt-6">{formulario}</CardContent>
      </Card>
    </div>
  );
};
